/**
 * Create / edit a PSIRT team — the organisational half every assigned product
 * inherits: Meldestelle, CVD-Policy and the Art. 14 reporting channel.
 */

import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { createCmdbCard, deleteCmdbCard, errorMessage, updateCmdbCard } from '../../lib/api';
import type { PsirtTeam, PsirtTeamValues } from '../../lib/types';

import PsirtChecklist from './PsirtChecklist';
import {
  CSIRT_SUGGESTIONS,
  TEAM_KEYS,
  emailProblem,
  erreichbarkeitSuggestions,
  euStates,
  filled,
  teamChecklist,
  toBody,
  urlProblem,
} from './psirtModel';

export interface TeamFormModalProps {
  /** `null` creates a new team. */
  team: PsirtTeam | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
}

type TextKey = Exclude<keyof PsirtTeamValues, never>;

export function TeamFormModal({ team, canWrite, onClose, onSaved }: TeamFormModalProps): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const toast = useToast();
  const [values, setValues] = useState<PsirtTeamValues>(() => ({
    Mitgliedstaat: 'DE',
    CsirtKoordinator: CSIRT_SUGGESTIONS.DE,
    ...(team?.values ?? {}),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [touched, setTouched] = useState(false);

  const readOnly = !canWrite || saving;
  // Checklist labels and country names are translated: rebuild them on a language switch.
  const checklist = useMemo(() => teamChecklist(values), [values, locale]);
  const states = useMemo(() => euStates(), [locale]);
  const reachabilityOptions = useMemo(() => erreichbarkeitSuggestions(), [locale]);

  const set = (key: TextKey, value: string): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const onStateChange = (code: string): void => {
    setValues((current) => {
      const previousSuggestion = CSIRT_SUGGESTIONS[current.Mitgliedstaat ?? ''] ?? '';
      const keepCsirt = filled(current.CsirtKoordinator) && current.CsirtKoordinator !== previousSuggestion;
      return {
        ...current,
        Mitgliedstaat: code,
        CsirtKoordinator: keepCsirt ? current.CsirtKoordinator : (CSIRT_SUGGESTIONS[code] ?? ''),
      };
    });
  };

  const problems: Partial<Record<TextKey, string | null>> = {
    MeldeEmail: emailProblem(values.MeldeEmail),
    MelderEmail: emailProblem(values.MelderEmail),
    LeitungEmail: emailProblem(values.LeitungEmail),
    VertretungEmail: emailProblem(values.VertretungEmail),
    CvdPolicyUrl: urlProblem(values.CvdPolicyUrl),
    SecurityTxtUrl: urlProblem(values.SecurityTxtUrl),
  };
  const nameMissing = !filled(values.Name);
  const hasProblems = nameMissing || Object.values(problems).some(Boolean);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (hasProblems || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      const body = toBody(values, TEAM_KEYS);
      if (team) await updateCmdbCard('PsirtTeam', team.id, body);
      else await createCmdbCard('PsirtTeam', body);
      toast.success(team ? t`PSIRT-Team gespeichert.` : t`PSIRT-Team angelegt.`);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (!team) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCmdbCard('PsirtTeam', team.id);
      toast.success(t`PSIRT-Team gelöscht.`);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
      setConfirmDelete(false);
    }
  }

  const text = (
    key: TextKey,
    label: string,
    options: { type?: string; placeholder?: string; hint?: ReactNode; required?: boolean; list?: string } = {},
  ): ReactNode => {
    const id = `psirt-team-${key}`;
    const problem = touched || filled(values[key]) ? problems[key] : null;
    const missing = options.required && touched && !filled(values[key]);
    return (
      <div className="field">
        <label htmlFor={id}>
          {label}
          {options.required ? ' *' : ''}
        </label>
        <input
          id={id}
          type={options.type ?? 'text'}
          value={String(values[key] ?? '')}
          placeholder={options.placeholder}
          list={options.list}
          disabled={readOnly}
          aria-invalid={Boolean(problem || missing) || undefined}
          onChange={(event) => set(key, event.target.value)}
        />
        {options.hint ? <span className="hint">{options.hint}</span> : null}
        {problem ? <span className="err">{problem}</span> : null}
        {missing ? <span className="err"><Trans>Pflichtfeld</Trans></span> : null}
      </div>
    );
  };

  const blockedDelete = (team?.produkte ?? 0) > 0;

  return (
    <Modal
      title={team ? t`PSIRT-Team bearbeiten` : t`PSIRT-Team anlegen`}
      subtitle={
        team
          ? team.label
          : t`Die Angaben gelten für jedes Produkt, das diesem Team zugeordnet wird.`
      }
      size="wide"
      dismissable={!saving}
      onClose={onClose}
      footer={
        <>
          {team && canWrite ? (
            confirmDelete ? (
              <button type="button" className="danger" onClick={() => void remove()} disabled={saving}>
                <Trans>Wirklich löschen?</Trans>
              </button>
            ) : (
              <button
                type="button"
                className="ghost"
                onClick={() => setConfirmDelete(true)}
                disabled={saving || blockedDelete}
                title={
                  blockedDelete
                    ? t`${plural(team.produkte, {
                        one: 'Noch # Produkt zugeordnet. Weisen Sie es zuerst einem anderen Team zu.',
                        other: 'Noch # Produkte zugeordnet. Weisen Sie diese zuerst einem anderen Team zu.',
                      })}`
                    : undefined
                }
              >
                <Trans>Team löschen</Trans>
              </button>
            )
          ) : null}
          <span className="spacer" />
          <button type="button" onClick={onClose} disabled={saving}>
            {canWrite ? t`Abbrechen` : t`Schließen`}
          </button>
          {canWrite ? (
            <button type="submit" className="primary" form="psirt-team-form" disabled={saving}>
              {saving ? <Spinner variant="on-accent" label={t`Speichert…`} /> : team ? t`Speichern` : t`Team anlegen`}
            </button>
          ) : null}
        </>
      }
    >
      <form id="psirt-team-form" className="psirt-form" onSubmit={submit} noValidate>
        <div className="psirt-form-main">
          {error ? (
            <div className="banner error" role="alert">
              {error}
            </div>
          ) : null}

          <section className="psirt-section">
            <header>
              <h3><Trans>Team</Trans></h3>
            </header>
            <div className="psirt-grid">
              {text('Name', t`Teamname`, { placeholder: t`z. B. Musterfirma PSIRT`, required: true })}
              {text('Erreichbarkeit', t`Erreichbarkeit`, {
                placeholder: t`24/7 Rufbereitschaft`,
                list: 'psirt-erreichbarkeit',
                hint: t`Die 24-h-Frühwarnung läuft auch am Wochenende.`,
              })}
              {text('Telefon', t`Telefon / Rufbereitschaft`, { type: 'tel', placeholder: t`+49 …` })}
            </div>
            <datalist id="psirt-erreichbarkeit">
              {reachabilityOptions.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </section>

          <section className="psirt-section">
            <header>
              <h3><Trans>Meldestelle &amp; Offenlegung</Trans></h3>
              <p><Trans>Gehört in die Produktinformation (Anhang II Nr. 2) und in die security.txt.</Trans></p>
            </header>
            <div className="psirt-grid">
              {text('MeldeEmail', t`Melde-Adresse (E-Mail)`, { type: 'email', placeholder: t`psirt@musterfirma.de` })}
              {text('CvdPolicyUrl', t`CVD-Policy (URL)`, {
                type: 'url',
                placeholder: t`https://musterfirma.de/security/cvd`,
                hint: t`Richtlinie zur koordinierten Offenlegung (Anhang I Teil II Nr. 5).`,
              })}
              {text('SecurityTxtUrl', t`security.txt (URL)`, {
                type: 'url',
                placeholder: t`https://musterfirma.de/.well-known/security.txt`,
                hint: t`Empfohlen, RFC 9116.`,
              })}
            </div>
          </section>

          <section className="psirt-section">
            <header>
              <h3><Trans>Meldeweg nach Art. 14</Trans></h3>
              <p>
                <Trans>
                  Meldungen laufen über die ENISA-Meldeplattform an das CSIRT des Mitgliedstaats der
                  Hauptniederlassung: Frühwarnung in 24 h, Meldung in 72 h, danach Abschlussbericht.
                </Trans>
              </p>
            </header>
            <div className="psirt-grid">
              <div className="field">
                <label htmlFor="psirt-team-Mitgliedstaat"><Trans>Mitgliedstaat der Hauptniederlassung</Trans></label>
                <select
                  id="psirt-team-Mitgliedstaat"
                  value={values.Mitgliedstaat ?? ''}
                  disabled={readOnly}
                  onChange={(event) => onStateChange(event.target.value)}
                >
                  <option value="">{t`— wählen —`}</option>
                  {states.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              {text('CsirtKoordinator', t`Koordinierendes CSIRT`, {
                placeholder: t`laut nationaler Benennung`,
              })}
              {text('MelderName', t`Meldeverantwortliche(r)`, { placeholder: t`Vor- und Nachname` })}
              {text('MelderEmail', t`E-Mail der/des Meldeverantwortlichen`, { type: 'email' })}
            </div>
          </section>

          <section className="psirt-section">
            <header>
              <h3><Trans>Leitung &amp; Stellvertretung</Trans></h3>
            </header>
            <div className="psirt-grid psirt-grid-pairs">
              {text('LeitungName', t`PSIRT-Leitung`, { placeholder: t`Vor- und Nachname` })}
              {text('LeitungEmail', t`E-Mail`, { type: 'email' })}
              {text('VertretungName', t`Stellvertretung`, { placeholder: t`Vor- und Nachname` })}
              {text('VertretungEmail', t`E-Mail`, { type: 'email' })}
            </div>
          </section>

          <section className="psirt-section">
            <div className="field">
              <label htmlFor="psirt-team-Bemerkung"><Trans>Bemerkung</Trans></label>
              <textarea
                id="psirt-team-Bemerkung"
                rows={3}
                value={values.Bemerkung ?? ''}
                disabled={readOnly}
                onChange={(event) => set('Bemerkung', event.target.value)}
              />
            </div>
          </section>
        </div>

        <PsirtChecklist
          title={t`CRA-Anforderungen`}
          items={checklist}
          footnote={t`Jedes Produkt dieses Teams übernimmt Meldestelle, CVD-Policy und Meldeweg.`}
        />
      </form>
    </Modal>
  );
}

export default TeamFormModal;
